import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */

/* @jsx mdx */

import DefaultLayout from "/home/runner/work/commerce-php/commerce-php/node_modules/@adobe/gatsby-theme-aio/src/components/MDXFilter/index.js";
export const _frontmatter = {};

const makeShortcode = name => function MDXDefaultShortcode(props) {
  console.warn("Component " + name + " was not imported, exported, or provided by MDXProvider as global scope");
  return <div {...props} />;
};

const InlineAlert = makeShortcode("InlineAlert");
const layoutProps = {
  _frontmatter
};
const MDXLayout = DefaultLayout;
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">


    <h1 {...{
      "id": "plugins"
    }}>{`Plugins`}</h1>
    <p>{`A plugin, or interceptor, is a class that modifies the behavior of public class functions by intercepting a function call and running code before, after, or around that function call. This allows you to `}<em parentName="p">{`substitute`}</em>{` or `}<em parentName="p">{`extend`}</em>{` the behavior of original, public methods for any `}<em parentName="p">{`class`}</em>{` or `}<em parentName="p">{`interface`}</em>{`.`}</p>
    <p>{`Extensions that wish to intercept and change the behavior of a `}<em parentName="p">{`public method`}</em>{` can create a `}<inlineCode parentName="p">{`Plugin`}</inlineCode>{` class.`}</p>
    <p>{`This interception approach reduces conflicts among extensions that change the behavior of the same class or method. Your `}<inlineCode parentName="p">{`Plugin`}</inlineCode>{` class implementation changes the behavior of a class function, but it does not change the class itself. Adobe Commerce and Magento Open Source call these interceptors sequentially according to a configured sort order, so they do not conflict with one another.`}</p>
    <h2 {...{
      "id": "limitations"
    }}>{`Limitations`}</h2>
    <p>{`Plugins can not be used on following:`}</p>
    <ul>
      <li parentName="ul">{`Final methods`}</li>
      <li parentName="ul">{`Final classes`}</li>
      <li parentName="ul">{`Non-public methods`}</li>
      <li parentName="ul">{`Class methods (such as static methods)`}</li>
      <li parentName="ul"><inlineCode parentName="li">{`__construct`}</inlineCode>{` and `}<inlineCode parentName="li">{`__destruct`}</inlineCode></li>
      <li parentName="ul">{`Virtual types`}</li>
      <li parentName="ul">{`Objects that are instantiated before `}<inlineCode parentName="li">{`Magento\\Framework\\Interception`}</inlineCode>{` is bootstrapped`}</li>
      <li parentName="ul">{`Objects that implement `}<inlineCode parentName="li">{`Magento\\Framework\\ObjectManager\\NoninterceptableInterface`}</inlineCode></li>
    </ul>
    <h2 {...{
      "id": "declaring-a-plugin"
    }}>{`Declaring a plugin`}</h2>
    <p>{`The `}<inlineCode parentName="p">{`di.xml`}</inlineCode>{` file in your module declares a plugin for a class object:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-xml"
      }}>{`<config>
    <type name="{ObservedType}">
      <plugin name="{pluginName}" type="{PluginClassName}" sortOrder="1" disabled="false" />
    </type>
</config>
`}</code></pre>
    <p>{`You must specify these elements:`}</p>
    <ul>
      <li parentName="ul"><inlineCode parentName="li">{`type name`}</inlineCode>{`. A class or interface which the plugin observes.`}</li>
      <li parentName="ul"><inlineCode parentName="li">{`plugin name`}</inlineCode>{`. An arbitrary plugin name that identifies a plugin. Also used to merge the configurations for the plugin.`}</li>
      <li parentName="ul"><inlineCode parentName="li">{`plugin type`}</inlineCode>{`. The name of a plugin's class or its virtual type. Use the following naming convention when you specify this element: `}<inlineCode parentName="li">{`\\Vendor\\Module\\Plugin\\<ClassName>`}</inlineCode>{`.`}</li>
    </ul>
    <p>{`The following elements are optional:`}</p>
    <ul>
      <li parentName="ul"><inlineCode parentName="li">{`plugin sortOrder`}</inlineCode>{`. Plugins that call the same method run them using this order.`}</li>
      <li parentName="ul"><inlineCode parentName="li">{`plugin disabled`}</inlineCode>{`. To disable a plugin, set this element to `}<inlineCode parentName="li">{`true`}</inlineCode>{`. The default value is `}<inlineCode parentName="li">{`false`}</inlineCode>{`.`}</li>
    </ul>
    <h3 {...{
      "id": "defining-a-plugin"
    }}>{`Defining a plugin`}</h3>
    <p>{`By applying code before, after, or around a public method, a plugin extends or modifies that method's behavior.`}</p>
    <p>{`The first argument for the before, after, and around methods is an object that provides access to all public methods of the observed method's class.`}</p>
    <h3 {...{
      "id": "plugin-method-naming-convention"
    }}>{`Plugin method naming convention`}</h3>
    <p>{`It is a best practice to capitalize the first letter of the class method name for which you want to create a plugin before adding `}<inlineCode parentName="p">{`before`}</inlineCode>{`, `}<inlineCode parentName="p">{`around`}</inlineCode>{` or `}<inlineCode parentName="p">{`after`}</inlineCode>{` prefixes to it.`}</p>
    <p>{`For example, to create a plugin for the `}<inlineCode parentName="p">{`setName`}</inlineCode>{` method of some class:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-php"
      }}>{`...
    public function setName($name)
    {
        ...
    }
...
`}</code></pre>
    <p>{`In the plugin class, the `}<inlineCode parentName="p">{`setName`}</inlineCode>{` method may have one of the following names:`}</p>
    <ul>
      <li parentName="ul"><inlineCode parentName="li">{`beforeSetName`}</inlineCode></li>
      <li parentName="ul"><inlineCode parentName="li">{`aroundSetName`}</inlineCode></li>
      <li parentName="ul"><inlineCode parentName="li">{`afterSetName`}</inlineCode></li>
    </ul>
    <p>{`If the first letter in the name of the class method name for which you want to create a plugin is the `}<inlineCode parentName="p">{`underscore`}</inlineCode>{` character, then you do not need to capitalize it in the plugin class.`}</p>
    <p>{`For example, to create a plugin for the `}<inlineCode parentName="p">{`_construct`}</inlineCode>{` method of some class:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-php"
      }}>{`...
    public function _construct()
    {
        ...
    }
...
`}</code></pre>
    <p>{`Use the following method names for the `}<inlineCode parentName="p">{`_construct`}</inlineCode>{` method in the plugin class:`}</p>
    <ul>
      <li parentName="ul"><inlineCode parentName="li">{`before_construct`}</inlineCode></li>
      <li parentName="ul"><inlineCode parentName="li">{`around_construct`}</inlineCode></li>
      <li parentName="ul"><inlineCode parentName="li">{`after_construct`}</inlineCode></li>
    </ul>
    <h2 {...{
      "id": "before-methods"
    }}>{`Before methods`}</h2>
    <p>{`The application runs all before methods ahead of the call to an observed method. These methods must have the same name as the observed method with 'before' as the prefix.`}</p>
    <p>{`You can use before methods to change the arguments of an observed method by returning a modified argument. If there are any arguments, the method should return an array of those arguments. If the method does not change the argument for the observed method, it should return a `}<inlineCode parentName="p">{`null`}</inlineCode>{` value.`}</p>
    <p>{`Below is an example of a before method modifying the `}<inlineCode parentName="p">{`$name`}</inlineCode>{` argument before passing it on to the observed `}<inlineCode parentName="p">{`setName`}</inlineCode>{` method.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-php"
      }}>{`<?php
/**
 * Copyright [first year code created] Adobe
 * All rights reserved.
 */

namespace My\\Module\\Plugin;

use Magento\\Catalog\\Model\\Product;

class ProductAttributesUpdater
{
    public function beforeSetName(Product $subject, $name)
    {
        return ['(' . $name . ')'];
    }
}
`}</code></pre>
    <h2 {...{
      "id": "after-methods"
    }}>{`After methods`}</h2>
    <p>{`The application runs all after methods following the completion of the observed method. The application requires these methods have a return value and they must have the same name as the observed method with 'after' as the prefix.`}</p>
    <p>{`You can use these methods to change the result of an observed method by modifying the original result and returning it at the end of the method.`}</p>
    <p>{`Below is an example of an after method modifying the return value `}<inlineCode parentName="p">{`$result`}</inlineCode>{` of an observed methods call.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-php"
      }}>{`<?php
/**
 * Copyright [first year code created] Adobe
 * All rights reserved.
 */

namespace My\\Module\\Plugin;

use Magento\\Catalog\\Model\\Product;

class ProductAttributesUpdater
{
    public function afterGetName(Product $subject, $result)
    {
        return '|' . $result . '|';
    }
}
`}</code></pre>
    <p>{`The after methods have access to all the arguments of their observed methods. When the observed method completes, the application passes the result and arguments to the next after method that follows. If the observed method does not return a result (`}<inlineCode parentName="p">{`@return void`}</inlineCode>{`), then it passes a `}<inlineCode parentName="p">{`null`}</inlineCode>{` value to the next after method.`}</p>
    <p>{`Below is an example of an after method that accepts the `}<inlineCode parentName="p">{`null`}</inlineCode>{` result and arguments from the observed `}<inlineCode parentName="p">{`login`}</inlineCode>{` method for `}<a parentName="p" {...{
        "href": "https://github.com/magento/magento2/blob/2.4/app/code/Magento/Backend/Model/Auth.php"
      }}><inlineCode parentName="a">{`Magento\\Backend\\Model\\Auth`}</inlineCode></a>{`:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-php"
      }}>{`<?php
/**
 * Copyright [first year code created] Adobe
 * All rights reserved.
 */

namespace My\\Module\\Plugin;

use Magento\\Backend\\Model\\Auth;
use Psr\\Log\\LoggerInterface;

class AuthLogger
{
    private $logger;

    public function __construct(LoggerInterface $logger)
    {
        $this->logger = $logger;
    }

    /**
     * @param Auth $authModel
     * @param null $result
     * @param string $username
     * @return void
     * @SuppressWarnings(PHPMD.UnusedFormalParameter)
     */
    public function afterLogin(Auth $authModel, $result, $username)
    {
        $this->logger->debug('User ' . $username . ' signed in.');
    }
}
`}</code></pre>
    <p>{`After methods do not need to declare all the arguments of their observed methods except those that the method uses and any arguments from the observed method that come before those used arguments.`}</p>
    <p>{`The following example is a class with an after method for `}<a parentName="p" {...{
        "href": "https://github.com/magento/magento2/blob/2.4/app/code/Magento/Catalog/Model/Product/Action.php"
      }}><inlineCode parentName="a">{`\\Magento\\Catalog\\Model\\Product\\Action::updateWebsites($productIds, $websiteIds, $type)`}</inlineCode></a>{`:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-php"
      }}>{`<?php
/**
 * Copyright [first year code created] Adobe
 * All rights reserved.
 */

use Psr\\Log\\LoggerInterface;
use Magento\\Catalog\\Model\\Product\\Action;

class WebsitesLogger
{
    private $logger;

    public function __construct(LoggerInterface $logger)
    {
        $this->logger = $logger;
    }

    public function afterUpdateWebsites(Action $subject, $result, $productIds, $websiteIds)
    {
        $this->logger->log('Updated websites: ' . implode(', ',  $websiteIds));
    }
}

`}</code></pre>
    <p>{`In the example, the `}<inlineCode parentName="p">{`afterUpdateWebsites`}</inlineCode>{` function uses the variable `}<inlineCode parentName="p">{`$websiteIds`}</inlineCode>{`, so it declares that variable as an argument. It also declares `}<inlineCode parentName="p">{`$productIds`}</inlineCode>{` because it comes before `}<inlineCode parentName="p">{`$websiteIds`}</inlineCode>{` in the parameter signature of the observed method. The after method did not list `}<inlineCode parentName="p">{`$type`}</inlineCode>{` because it did not use it inside the method nor does it come before `}<inlineCode parentName="p">{`$websiteIds`}</inlineCode>{`.`}</p>
    <InlineAlert variant="warning" slots="text" mdxType="InlineAlert" />
    <p>{`If an argument is optional in the observed method, then the after method should also declare it as optional.`}</p>
    <h2 {...{
      "id": "around-methods"
    }}>{`Around methods`}</h2>
    <p>{`The application runs the code in around methods before and after their observed methods. Using these methods allow you to override an observed method. Around methods must have the same name as the observed method with 'around' as the prefix.`}</p>
    <InlineAlert variant="warning" slots="text" mdxType="InlineAlert" />
    <p>{`Avoid using around method plugins when they are not required because they increase stack traces and affect performance.
The only use case for around method plugins is when the execution of all further plugins and original methods need termination.
Use after method plugins if you require arguments for replacing or altering function results.`}</p>
    <p>{`Before the list of the original method's arguments, around methods receive a `}<inlineCode parentName="p">{`callable`}</inlineCode>{` that will allow a call to the next method in the chain. When your code executes the `}<inlineCode parentName="p">{`callable`}</inlineCode>{`, the application calls the next plugin or the observed function.`}</p>
    <InlineAlert variant="warning" slots="text" mdxType="InlineAlert" />
    <p>{`If the around method does not call the `}<inlineCode parentName="p">{`callable`}</inlineCode>{`, it will prevent the execution of all the plugins next in the chain and the original method call.`}</p>
    <p>{`Below is an example of an around method adding behavior before and after an observed method:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-php"
      }}>{`<?php
/**
 * Copyright [first year code created] Adobe
 * All rights reserved.
 */

namespace My\\Module\\Plugin;

use Magento\\Catalog\\Model\\Product;

class ProductAttributesUpdater
{
    public function aroundSave(Product $subject, callable $proceed)
    {
        $someValue = $this->doSmthBeforeProductIsSaved();
        $returnValue = null;

        if ($this->canCallProceedCallable($someValue)) {
            $returnValue = $proceed();
        }

        if ($returnValue) {
            $this->postProductToFacebook();
        }

        return $returnValue;
    }
}
`}</code></pre>
    <p>{`When you wrap a method which accepts arguments, your plugin must accept those arguments and you must forward them when you invoke the `}<inlineCode parentName="p">{`proceed`}</inlineCode>{` callable. You must be careful to match the default parameters and type hints of the original signature of the method.`}</p>
    <p>{`For example, the following code defines a parameter of type `}<inlineCode parentName="p">{`SomeType`}</inlineCode>{`, which is nullable:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-php"
      }}>{`<?php
/**
 * Copyright [first year code created] Adobe
 * All rights reserved.
 */

namespace My\\Module\\Model;

class MyUtility
{
    public function save(SomeType $obj = null)
    {
        //do something
    }
}
`}</code></pre>
    <p>{`You should wrap this method with a plugin:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-php"
      }}>{`<?php
/**
 * Copyright [first year code created] Adobe
 * All rights reserved.
 */

namespace My\\Module\\Plugin;

use My\\Module\\Model\\MyUtility;

class MyUtilityUpdater
{
    public function aroundSave(MyUtility $subject, callable $proceed, SomeType $obj = null)
    {
        //do something
    }
}
`}</code></pre>
    <p>{`Note if you miss `}<inlineCode parentName="p">{`= null`}</inlineCode>{` and the applicationcalls the original method with `}<inlineCode parentName="p">{`null`}</inlineCode>{`, PHP would throw a fatal error as your plugin does not accept `}<inlineCode parentName="p">{`null`}</inlineCode>{`.`}</p>
    <p>{`You are responsible for forwarding the arguments from the plugin to the `}<inlineCode parentName="p">{`proceed`}</inlineCode>{` callable. If you are not using/modifying the arguments, you could use variadics and argument unpacking to achieve this:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-php"
      }}>{`<?php
/**
 * Copyright [first year code created] Adobe
 * All rights reserved.
 */

namespace My\\Module\\Plugin;

use My\\Module\\Model\\MyUtility;

class MyUtilityUpdater
{
    public function aroundSave(MyUtility $subject, callable $proceed, ...$args)
    {
        //do something
        $proceed(...$args);
    }
}
`}</code></pre>
    <h2 {...{
      "id": "prioritizing-plugins"
    }}>{`Prioritizing plugins`}</h2>
    <p>{`The `}<inlineCode parentName="p">{`sortOrder`}</inlineCode>{` property from the `}<inlineCode parentName="p">{`plugin`}</inlineCode>{` node declared in `}<inlineCode parentName="p">{`di.xml`}</inlineCode>{` determines the plugin's prioritization when more than one plugin is observing the same method.`}</p>
    <p>{`The `}<a parentName="p" {...{
        "href": "https://github.com/magento/magento2/blob/2.4/lib/internal/Magento/Framework/Interception/PluginListInterface.php"
      }}><inlineCode parentName="a">{`Magento\\Framework\\Interception\\PluginListInterface`}</inlineCode></a>{` which is implemented by `}<a parentName="p" {...{
        "href": "https://github.com/magento/magento2/blob/2.4/lib/internal/Magento/Framework/Interception/PluginList/PluginList.php"
      }}><inlineCode parentName="a">{`Magento\\Framework\\Interception\\PluginList\\PluginList`}</inlineCode></a>{` is responsible to define when to call the before, around, or after methods respecting this prioritization.`}</p>
    <p>{`If two or more plugins have the same `}<inlineCode parentName="p">{`sortOrder`}</inlineCode>{` value or do not specify it, the `}<a parentName="p" {...{
        "href": "../build/component-load-order.md"
      }}>{`component load order`}</a>{` declared in the `}<inlineCode parentName="p">{`sequence`}</inlineCode>{` node from `}<inlineCode parentName="p">{`module.xml`}</inlineCode>{` and `}<a parentName="p" {...{
        "href": "../build/schema-validation.md"
      }}>{`area`}</a>{` will define the merge sequence. Check the component load order in `}<inlineCode parentName="p">{`app/etc/config.php`}</inlineCode>{` file.`}</p>
    <p>{`The application executes plugins using these rules during each plugin execution in two main flows:`}</p>
    <ul>
      <li parentName="ul">
        <p parentName="li">{`Before the execution of the observed method, starting from lowest to highest `}<inlineCode parentName="p">{`sortOrder`}</inlineCode>{`.`}</p>
        <ul parentName="li">
          <li parentName="ul">{`The application executes the current plugin's `}<inlineCode parentName="li">{`before`}</inlineCode>{` method.`}</li>
          <li parentName="ul">{`Then the current plugin's `}<inlineCode parentName="li">{`around`}</inlineCode>{` method is called.`}
            <ul parentName="li">
              <li parentName="ul">{`The first part of the plugin's `}<inlineCode parentName="li">{`around`}</inlineCode>{` method is executed.`}</li>
              <li parentName="ul">{`The `}<inlineCode parentName="li">{`around`}</inlineCode>{` method executes the `}<inlineCode parentName="li">{`callable`}</inlineCode>{`.`}
                <ul parentName="li">
                  <li parentName="ul">{`If there is another plugin in the chain, all subsequent plugins are wrapped in an independent sequence loop and the execution starts another flow.`}</li>
                  <li parentName="ul">{`If the current plugin is the last in the chain, the observed method is executed.`}</li>
                </ul>
              </li>
              <li parentName="ul">{`The second part of the `}<inlineCode parentName="li">{`around`}</inlineCode>{` method is executed.`}</li>
            </ul>
          </li>
          <li parentName="ul">{`The application moves on to the next plugin.`}</li>
        </ul>
      </li>
      <li parentName="ul">
        <p parentName="li">{`Following the execution flow, starting from lowest to highest `}<inlineCode parentName="p">{`sortOrder`}</inlineCode>{` in the current sequence plugins loop.`}</p>
        <ul parentName="li">
          <li parentName="ul">{`The current plugin's `}<inlineCode parentName="li">{`after`}</inlineCode>{` method is executed.`}</li>
          <li parentName="ul">{`The application moves on to the next plugin.`}</li>
        </ul>
      </li>
    </ul>
    <p>{`As a result of these rules, the execution flow of an observed method is affected not only by the prioritization of the plugins, but also by their implemented methods.`}</p>
    <InlineAlert variant="info" slots="text" mdxType="InlineAlert" />
    <p>{`The `}<inlineCode parentName="p">{`around`}</inlineCode>{` plugin's method affects the flow of all plugins that are executed after it.`}</p>
    <InlineAlert variant="success" slots="text" mdxType="InlineAlert" />
    <p>{`When the `}<inlineCode parentName="p">{`before`}</inlineCode>{` and `}<inlineCode parentName="p">{`around`}</inlineCode>{` plugin sequence is finished, the application calls the first plugin `}<inlineCode parentName="p">{`after`}</inlineCode>{` method in the sequence loop, and not the `}<inlineCode parentName="p">{`after`}</inlineCode>{` method of the current plugin that was being executed by the `}<inlineCode parentName="p">{`around`}</inlineCode>{` method.`}</p>
    <h3 {...{
      "id": "examples"
    }}>{`Examples`}</h3>
    <p>{`For example, the `}<inlineCode parentName="p">{`di.xml`}</inlineCode>{` file of your module attaches three plugins for the class `}<inlineCode parentName="p">{`Action`}</inlineCode>{`:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-xml"
      }}>{`<config>
    <type name="Magento\\Framework\\App\\Action\\Action">
        <plugin name="vendor_module_plugina" type="Vendor\\Module\\Plugin\\PluginA" sortOrder="10" />
        <plugin name="vendor_module_pluginb" type="Vendor\\Module\\Plugin\\PluginB" sortOrder="20" />
        <plugin name="vendor_module_pluginc" type="Vendor\\Module\\Plugin\\PluginC" sortOrder="30" />
    </type>
</config>
`}</code></pre>
    <p>{`The execution will have a different flow, depending on the methods implemented by these classes, as explained in the following scenarios.`}</p>
    <h4 {...{
      "id": "scenario-a"
    }}>{`Scenario A`}</h4>
    <p>{`With these methods:`}</p>
    <table>
      <thead parentName="table">
        <tr parentName="thead">
          <th parentName="tr" {...{
            "align": null
          }}></th>
          <th parentName="tr" {...{
            "align": null
          }}>{`PluginA`}</th>
          <th parentName="tr" {...{
            "align": null
          }}>{`PluginB`}</th>
          <th parentName="tr" {...{
            "align": null
          }}>{`PluginC`}</th>
        </tr>
      </thead>
      <tbody parentName="table">
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><strong parentName="td">{`sortOrder`}</strong></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`10`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`20`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`30`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><strong parentName="td">{`before`}</strong></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`beforeDispatch()`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`beforeDispatch()`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`beforeDispatch()`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><strong parentName="td">{`around`}</strong></td>
          <td parentName="tr" {...{
            "align": null
          }}></td>
          <td parentName="tr" {...{
            "align": null
          }}></td>
          <td parentName="tr" {...{
            "align": null
          }}></td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><strong parentName="td">{`after`}</strong></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`afterDispatch()`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`afterDispatch()`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`afterDispatch()`}</td>
        </tr>
      </tbody>
    </table>
    <p>{`The execution will be in this order:`}</p>
    <ul>
      <li parentName="ul">
        <p parentName="li"><inlineCode parentName="p">{`PluginA::beforeDispatch()`}</inlineCode></p>
      </li>
      <li parentName="ul">
        <p parentName="li"><inlineCode parentName="p">{`PluginB::beforeDispatch()`}</inlineCode></p>
      </li>
      <li parentName="ul">
        <p parentName="li"><inlineCode parentName="p">{`PluginC::beforeDispatch()`}</inlineCode></p>
        <ul parentName="li">
          <li parentName="ul"><inlineCode parentName="li">{`Action::dispatch()`}</inlineCode></li>
        </ul>
      </li>
      <li parentName="ul">
        <p parentName="li"><inlineCode parentName="p">{`PluginA::afterDispatch()`}</inlineCode></p>
      </li>
      <li parentName="ul">
        <p parentName="li"><inlineCode parentName="p">{`PluginB::afterDispatch()`}</inlineCode></p>
      </li>
      <li parentName="ul">
        <p parentName="li"><inlineCode parentName="p">{`PluginC::afterDispatch()`}</inlineCode></p>
      </li>
    </ul>
    <h4 {...{
      "id": "scenario-b-with-a-callable-around"
    }}>{`Scenario B (with a `}<inlineCode parentName="h4">{`callable`}</inlineCode>{` around)`}</h4>
    <p>{`With these methods:`}</p>
    <table>
      <thead parentName="table">
        <tr parentName="thead">
          <th parentName="tr" {...{
            "align": null
          }}></th>
          <th parentName="tr" {...{
            "align": null
          }}>{`PluginA`}</th>
          <th parentName="tr" {...{
            "align": null
          }}>{`PluginB`}</th>
          <th parentName="tr" {...{
            "align": null
          }}>{`PluginC`}</th>
        </tr>
      </thead>
      <tbody parentName="table">
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><strong parentName="td">{`sortOrder`}</strong></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`10`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`20`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`30`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><strong parentName="td">{`before`}</strong></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`beforeDispatch()`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`beforeDispatch()`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`beforeDispatch()`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><strong parentName="td">{`around`}</strong></td>
          <td parentName="tr" {...{
            "align": null
          }}></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`aroundDispatch()`}</td>
          <td parentName="tr" {...{
            "align": null
          }}></td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><strong parentName="td">{`after`}</strong></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`afterDispatch()`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`afterDispatch()`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`afterDispatch()`}</td>
        </tr>
      </tbody>
    </table>
    <p><inlineCode parentName="p">{`PluginB`}</inlineCode>{`::`}<inlineCode parentName="p">{`aroundDispatch()`}</inlineCode>{` defines the `}<a parentName="p" {...{
        "href": "https://github.com/magento/magento2/blob/2.4/lib/internal/Magento/Framework/Interception/Interceptor.php"
      }}>{`$next`}</a>{` argument with a `}<inlineCode parentName="p">{`callable`}</inlineCode>{` type. For example:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-php"
      }}>{`<?php
/**
 * Copyright [first year code created] Adobe
 * All rights reserved.
 */

use Magento\\Framework\\App\\Action\\Action;

class PluginB
{
    public function aroundDispatch(Action $subject, callable $next, ...$args)
    {
        // The first half of code goes here
        // ...

        $result = $next(...$args);

        // The second half of code goes here
        // ...

        return $result;
    }
}
`}</code></pre>
    <p>{`The execution will be in this order:`}</p>
    <ul>
      <li parentName="ul">
        <p parentName="li"><inlineCode parentName="p">{`PluginA::beforeDispatch()`}</inlineCode></p>
      </li>
      <li parentName="ul">
        <p parentName="li"><inlineCode parentName="p">{`PluginB::beforeDispatch()`}</inlineCode></p>
      </li>
      <li parentName="ul">
        <p parentName="li"><inlineCode parentName="p">{`PluginB::aroundDispatch()`}</inlineCode>{` (the application calls the first half before `}<inlineCode parentName="p">{`callable`}</inlineCode>{`)`}</p>
        <ul parentName="li">
          <li parentName="ul">
            <p parentName="li"><inlineCode parentName="p">{`PluginC::beforeDispatch()`}</inlineCode></p>
            <ul parentName="li">
              <li parentName="ul"><inlineCode parentName="li">{`Action::dispatch()`}</inlineCode></li>
            </ul>
          </li>
          <li parentName="ul">
            <p parentName="li"><inlineCode parentName="p">{`PluginC::afterDispatch()`}</inlineCode></p>
          </li>
        </ul>
      </li>
      <li parentName="ul">
        <p parentName="li"><inlineCode parentName="p">{`PluginB::aroundDispatch()`}</inlineCode>{` (the applicationcalls the second half after `}<inlineCode parentName="p">{`callable`}</inlineCode>{`)`}</p>
      </li>
      <li parentName="ul">
        <p parentName="li"><inlineCode parentName="p">{`PluginA::afterDispatch()`}</inlineCode></p>
      </li>
      <li parentName="ul">
        <p parentName="li"><inlineCode parentName="p">{`PluginB::afterDispatch()`}</inlineCode></p>
      </li>
    </ul>
    <h4 {...{
      "id": "scenario-b-without-a-callable-around"
    }}>{`Scenario B (without a `}<inlineCode parentName="h4">{`callable`}</inlineCode>{` around)`}</h4>
    <p>{`Using these methods:`}</p>
    <table>
      <thead parentName="table">
        <tr parentName="thead">
          <th parentName="tr" {...{
            "align": null
          }}></th>
          <th parentName="tr" {...{
            "align": null
          }}>{`PluginA`}</th>
          <th parentName="tr" {...{
            "align": null
          }}>{`PluginB`}</th>
          <th parentName="tr" {...{
            "align": null
          }}>{`PluginC`}</th>
        </tr>
      </thead>
      <tbody parentName="table">
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><strong parentName="td">{`sortOrder`}</strong></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`10`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`20`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`30`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><strong parentName="td">{`before`}</strong></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`beforeDispatch()`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`beforeDispatch()`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`beforeDispatch()`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><strong parentName="td">{`around`}</strong></td>
          <td parentName="tr" {...{
            "align": null
          }}></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`aroundDispatch()`}</td>
          <td parentName="tr" {...{
            "align": null
          }}></td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><strong parentName="td">{`after`}</strong></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`afterDispatch()`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`afterDispatch()`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`afterDispatch()`}</td>
        </tr>
      </tbody>
    </table>
    <p><inlineCode parentName="p">{`PluginB`}</inlineCode>{`::`}<inlineCode parentName="p">{`aroundDispatch()`}</inlineCode>{` does not define the `}<a parentName="p" {...{
        "href": "https://github.com/magento/magento2/blob/2.4/lib/internal/Magento/Framework/Interception/Interceptor.php"
      }}>{`$next`}</a>{` argument with a `}<inlineCode parentName="p">{`callable`}</inlineCode>{` type. For example:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-php"
      }}>{`<?php
/**
 * Copyright [first year code created] Adobe
 * All rights reserved.
 */

use Magento\\Framework\\App\\Action\\Action;

class PluginB
{
    public function aroundDispatch(Action $subject, $next, $result)
    {
        // My custom code
        return $result;
    }
}
`}</code></pre>
    <p>{`The execution will be in this order:`}</p>
    <ul>
      <li parentName="ul">
        <p parentName="li"><inlineCode parentName="p">{`PluginA::beforeDispatch()`}</inlineCode></p>
      </li>
      <li parentName="ul">
        <p parentName="li"><inlineCode parentName="p">{`PluginB::beforeDispatch()`}</inlineCode></p>
        <ul parentName="li">
          <li parentName="ul"><inlineCode parentName="li">{`PluginB::aroundDispatch()`}</inlineCode></li>
        </ul>
      </li>
      <li parentName="ul">
        <p parentName="li"><inlineCode parentName="p">{`PluginA::afterDispatch()`}</inlineCode></p>
      </li>
      <li parentName="ul">
        <p parentName="li"><inlineCode parentName="p">{`PluginB::afterDispatch()`}</inlineCode></p>
      </li>
    </ul>
    <p>{`Because the`}<inlineCode parentName="p">{`callable`}</inlineCode>{` type for the `}<inlineCode parentName="p">{`$next`}</inlineCode>{` agrument is absent, `}<inlineCode parentName="p">{`Action::dispatch()`}</inlineCode>{` will be not called and `}<inlineCode parentName="p">{`Plugin C`}</inlineCode>{` will be not triggered.`}</p>
    <h4 {...{
      "id": "scenario-c"
    }}>{`Scenario C`}</h4>
    <p>{`Assuming these methods:`}</p>
    <table>
      <thead parentName="table">
        <tr parentName="thead">
          <th parentName="tr" {...{
            "align": null
          }}></th>
          <th parentName="tr" {...{
            "align": null
          }}>{`PluginA`}</th>
          <th parentName="tr" {...{
            "align": null
          }}>{`PluginB`}</th>
          <th parentName="tr" {...{
            "align": null
          }}>{`PluginC`}</th>
        </tr>
      </thead>
      <tbody parentName="table">
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><strong parentName="td">{`sortOrder`}</strong></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`10`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`20`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`30`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><strong parentName="td">{`before`}</strong></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`beforeDispatch()`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`beforeDispatch()`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`beforeDispatch()`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><strong parentName="td">{`around`}</strong></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`aroundDispatch()`}</td>
          <td parentName="tr" {...{
            "align": null
          }}></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`aroundDispatch()`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><strong parentName="td">{`after`}</strong></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`afterDispatch()`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`afterDispatch()`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`afterDispatch()`}</td>
        </tr>
      </tbody>
    </table>
    <p>{`The execution will be in this order:`}</p>
    <ul>
      <li parentName="ul">
        <p parentName="li"><inlineCode parentName="p">{`PluginA::beforeDispatch()`}</inlineCode></p>
      </li>
      <li parentName="ul">
        <p parentName="li"><inlineCode parentName="p">{`PluginA::aroundDispatch()`}</inlineCode>{` (the application calls the first half until `}<inlineCode parentName="p">{`callable`}</inlineCode>{`)`}</p>
        <ul parentName="li">
          <li parentName="ul">
            <p parentName="li"><inlineCode parentName="p">{`PluginB::beforeDispatch()`}</inlineCode></p>
          </li>
          <li parentName="ul">
            <p parentName="li"><inlineCode parentName="p">{`PluginC::beforeDispatch()`}</inlineCode></p>
          </li>
          <li parentName="ul">
            <p parentName="li"><inlineCode parentName="p">{`PluginC::aroundDispatch()`}</inlineCode>{` (the application calls the first half until `}<inlineCode parentName="p">{`callable`}</inlineCode>{`)`}</p>
            <ul parentName="li">
              <li parentName="ul"><inlineCode parentName="li">{`Action::dispatch()`}</inlineCode></li>
            </ul>
          </li>
          <li parentName="ul">
            <p parentName="li"><inlineCode parentName="p">{`PluginC::aroundDispatch()`}</inlineCode>{` (the application calls the second half after `}<inlineCode parentName="p">{`callable`}</inlineCode>{`)`}</p>
          </li>
          <li parentName="ul">
            <p parentName="li"><inlineCode parentName="p">{`PluginB::afterDispatch()`}</inlineCode></p>
          </li>
          <li parentName="ul">
            <p parentName="li"><inlineCode parentName="p">{`PluginC::afterDispatch()`}</inlineCode></p>
          </li>
        </ul>
      </li>
      <li parentName="ul">
        <p parentName="li"><inlineCode parentName="p">{`PluginA::aroundDispatch()`}</inlineCode>{` (the application calls the second half after `}<inlineCode parentName="p">{`callable`}</inlineCode>{`)`}</p>
      </li>
      <li parentName="ul">
        <p parentName="li"><inlineCode parentName="p">{`PluginA::afterDispatch()`}</inlineCode></p>
      </li>
    </ul>
    <h2 {...{
      "id": "configuration-inheritance"
    }}>{`Configuration inheritance`}</h2>
    <p>{`Classes and interfaces that are implementations of, or inherit from, classes that have plugins will also inherit plugins from the parent class.`}</p>
    <p>{`The application uses plugins defined in the global scope when the system is in a specific area (such as frontend or backend). You can extend or override these global plugin configurations with an area's `}<inlineCode parentName="p">{`di.xml`}</inlineCode>{` file.`}</p>
    <p>{`For example, the developer can disable a global plugin in the backend area by disabling it in the specific `}<inlineCode parentName="p">{`di.xml`}</inlineCode>{` file for the backend area.`}</p>
    <h3 {...{
      "id": "disabling-plugins"
    }}>{`Disabling plugins`}</h3>
    <p>{`Plugins can be disabled in a `}<inlineCode parentName="p">{`di.xml`}</inlineCode>{` file. To disable a plugin, set the `}<inlineCode parentName="p">{`disabled`}</inlineCode>{` parameter of the plugin declaration  to `}<inlineCode parentName="p">{`true`}</inlineCode>{`.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-xml"
      }}>{`<type name="Magento\\Checkout\\Block\\Checkout\\LayoutProcessor">
    <plugin name="ProcessPaymentConfiguration" disabled="true"/>
</type>
`}</code></pre>
    <p>{`where `}<inlineCode parentName="p">{`ProcessPaymentConfiguration`}</inlineCode>{` is the name of the plugin declared in the `}<inlineCode parentName="p">{`vendor/magento/module-payment/etc/frontend/di.xml`}</inlineCode>{`.`}</p>
    <p>{`Note that the same class can be called two ways: with the leading slash or without.`}</p>
    <p><inlineCode parentName="p">{`\\Magento\\Checkout\\Block\\Checkout\\LayoutProcessor`}</inlineCode></p>
    <p>{`and`}</p>
    <p><inlineCode parentName="p">{`Magento\\Checkout\\Block\\Checkout\\LayoutProcessor`}</inlineCode></p>
    <p>{`are both valid.`}</p>
    <p>{`When disabling the plugin, make sure to use the same path format to call and disable the plugin.`}</p>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      